import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Text, View } from '../../../../../components/Common/react-native-components';
import { get, noop } from 'lodash';
import ExpandableGrid from '../../../../../components/ExpandableGrid/ExapandableGridWrapper';
import MultiSelect from '../../../../Filters/component/MultiSelect';
import { ActivityIndicator } from '@hmsexp/component-library';
import { StyleSheet } from 'react-native';
import useExpandableGridHelper from '../../../hooks/useExpandableGridHelper';
import { processColumns } from '../TopicTrendsOverTime/utils';
import GenericNoData from '../../../../../components/Common/NoData/GenricNoData';

const buildCacheKey = selectionsObj =>
  Object.values(selectionsObj)
    .map(v => v?.[0]?.fullValue)
    .join('-');

const MultiMetricTable = forwardRef((props, ref) => {
  const { itemData, applied_filter_values, widgetId, reduxData, hasNestedData, fetchNestedData = noop } = props;
  const dispatch = useDispatch();

  const dropdownConfigs = useMemo(() => itemData?.dropdownConfigs || [], [itemData]);

  const [selections, setSelections] = useState(() =>
    dropdownConfigs.reduce((acc, cfg) => {
      acc[cfg.key] = cfg.defaultValue ? [cfg.defaultValue] : [];
      return acc;
    }, {})
  );

  const [displayMetricKey, setDisplayMetricKey] = useState(buildCacheKey(selections));
  const tableData = get(reduxData, `${widgetId}.tableData`, {});
  const tableColumns = get(reduxData, `${widgetId}.tableData.${buildCacheKey(selections)}.columns`, []);

  const { fetchData, error, loadingData, setTableColumns } = useExpandableGridHelper({
    widgetId,
    appliedFilterValues: applied_filter_values,
    onAggregationResponseFn: ({ tableData, widgetId, tableColumns }) => {
      const cacheKey = buildCacheKey(selections);
      //   dispatch({
      //     type: 'SAVE_WIDGET_TABLE_DATA',
      //     payload: {
      //       widgetId,
      //       metricType: cacheKey,
      //       tableData,
      //       tableColumns,
      //       cacheKey: 'none-none-1'
      //     }
      //   });
    }
  });

  const buildWidgetFilters = ({ extraFilters, selections }) => {
    return {
      topic_filter: {},
      topic_depth: 'l1',
      ...Object.entries(selections).reduce((acc, [key, value]) => {
        acc[key] = value?.[0]?.fullValue;
        return acc;
      }, {}),
      ...extraFilters
    };
  };

  const handleSelectionChange = async (key, selectedOption) => {
    const updatedSelections = {
      ...selections,
      [key]: selectedOption
    };

    setSelections(updatedSelections);

    const nextMetricKey = buildCacheKey(updatedSelections);

    if (reduxData?.[widgetId]?.tableData?.[nextMetricKey]) {
      setDisplayMetricKey(nextMetricKey);
      return;
    }

    const widget_config = buildWidgetFilters({
      selections: updatedSelections,
      extraFilters: itemData?.defaultPaginationConfig
    });
    const { tableData, columns, totalRecords } = await fetchData({ widget_config });

    const config = get(itemData, 'metricBasedConfigs', {});
    const matchedKey = Object.keys(config).find(k => nextMetricKey.includes(k));
    const newColumns = processColumns(columns, matchedKey ? config[matchedKey] : {});

    dispatch({
      type: 'SAVE_WIDGET_TABLE_DATA',
      payload: {
        widgetId,
        metricType: nextMetricKey,
        tableData,
        totalRecords,
        tableColumns: itemData?.columns ? itemData?.columns : newColumns ?? columns,
        cacheKey: 'none-none-1'
      }
    });
    setDisplayMetricKey(nextMetricKey);
  };

  useEffect(() => {
    if (dropdownConfigs.length) {
      handleSelectionChange(dropdownConfigs[0].key, selections[dropdownConfigs[0].key]);
    }
  }, [dropdownConfigs]);

  return (
    <View>
      {loadingData && (
        <View style={styles.overlay}>
          <ActivityIndicator height={40} width={40} isDarkBg={false} />
        </View>
      )}

      <View style={styles.controlsContainer}>
        {!error && dropdownConfigs && (
          <View style={styles.controlsInner}>
            {dropdownConfigs.map(cfg => (
              <View style={styles.selectGroup} key={cfg.key}>
                <Text style={styles.label}>{cfg.label}</Text>
                <MultiSelect
                  mode='single'
                  options={cfg.options}
                  placeholder='Select...'
                  selected={selections[cfg.key]}
                  onSelect={opt => handleSelectionChange(cfg.key, opt)}
                />
              </View>
            ))}
          </View>
        )}
      </View>

      {error && <GenericNoData />}

      {!error && (
        <View style={styles.tableWrapper}>
          <ExpandableGrid
            loading={false}
            fetchData={fetchData}
            getNestedData={fetchNestedData}
            columns={tableColumns}
            widgetSpecificKeys={{ widgetId, ...buildWidgetFilters({ selections }) }}
            metricKey={displayMetricKey}
            dataCache={tableData || {}}
            showPagination={true}
            pageSize={itemData?.pageSize ?? 10}
            hasNestedData={hasNestedData}
          />
        </View>
      )}
    </View>
  );
});

export default MultiMetricTable;

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',

    alignItems: 'center',
    justifyContent: 'center',

    // 🚫 Block all clicks underneath
    pointerEvents: 'auto'
  },
  loaderContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 16,
    width: '100%'
  },
  controlsInner: {
    flexDirection: 'row',
    gap: 8,
    flex: 1
  },
  selectGroup: {
    marginBottom: 12,
    width: '25%'
  },
  label: {
    marginBottom: 8,
    fontWeight: '400',
    color: '#6b7589'
  },
  tableWrapper: {
    height: 'auto'
  }
});
